import type { StringKey } from '@ipfabric/utils';
import { FormField } from 'components/FormField/FormField';
import {
  type GetSwitchOptionValueFunction,
  Switch,
  type SwitchProps,
  getSwitchOptionValueDefault,
} from 'components/Switch/Switch';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

export type Props<TFormikValues, TValue> = Omit<
  SwitchProps<TValue>,
  'value' | 'onChange'
> & {
  disabled?: boolean;
  formik: FormikProps<TFormikValues>;
  label: ReactNode;
  name: StringKey<TFormikValues> | NestedFormikName;
  size?: 'normal' | 'small';
};

export const FormikSwitchField = <TFormikValues, TValue>({
  getOptionValue = getSwitchOptionValueDefault as GetSwitchOptionValueFunction<TValue>,
  ...props
}: Props<TFormikValues, TValue>): JSX.Element => {
  const field = props.formik.getFieldProps(props.name);
  const hasError = fieldHasError<TFormikValues>(props.formik, props.name);

  const handleChange = (value: TValue) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
  };

  return (
    <FormField
      label={props.label}
      error={hasError ? (props.formik.errors[props.name] as string) : undefined}
    >
      <Switch
        disabled={props.disabled}
        options={props.options}
        value={field.value}
        onChange={handleChange}
        hasError={hasError}
        getOptionValue={getOptionValue}
        size={props.size}
      />
    </FormField>
  );
};
